import { jsx, jsxs } from "react/jsx-runtime";
import { c } from "react/compiler-runtime";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme, styled } from "@mui/material/styles";
import { BarChart } from "@mui/x-charts/BarChart";
import { transposedDataToSeries, transposeRowsToSeries } from "./seriesMapper.js";
import { makeValueFormatter, getYAxisWidth } from "../utils/data.js";
const StyledBarChart = styled(BarChart, {
  shouldForwardProp: prop => !["showLabels"].includes(prop)
})(({
  theme
}) => ({
  display: "flex",
  flexDirection: "column",
  padding: theme.sizes.spacing["01"],
  margin: theme.sizes.spacing["01"],
  position: "relative",
  ["& .MuiBarElement-root"]: {
    strokeWidth: 1
  },
  ["& .MuiChartsAxis-root"]: {
    "& .MuiChartsAxis-line, & .MuiChartsAxis-tick, .MuiChartsAxis-label": {
      display: "none"
    }
  }
}));
const StackedBarChart = t0 => {
  const $ = c(90);
  const {
    title,
    labels: t1,
    chartProps,
    dataset,
    height,
    showLabels: t2,
    isTransposedData: t3,
    isLoading: t4,
    colorScheme: t5
  } = t0;
  let t6;
  if ($[0] !== t1) {
    t6 = t1 === void 0 ? {} : t1;
    $[0] = t1;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const labels = t6;
  const showLabels = t2 === void 0 ? false : t2;
  const isTransposedData = t3 === void 0 ? false : t3;
  const isLoading = t4 === void 0 ? false : t4;
  const colorScheme = t5 === void 0 ? "sand" : t5;
  const theme = useTheme();
  const xKey = chartProps.configuration.x.data.key;
  let t7;
  if ($[2] !== dataset) {
    t7 = dataset ?? {
      data: [],
      columns: []
    };
    $[2] = dataset;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const {
    data,
    columns
  } = t7;
  dataset?.data?.length;
  let t8;
  bb0: {
    if (!dataset?.data?.length || !columns?.length || isLoading) {
      let t93;
      if ($[4] === /* @__PURE__ */Symbol.for("react.memo_cache_sentinel")) {
        t93 = [];
        $[4] = t93;
      } else {
        t93 = $[4];
      }
      t8 = t93;
      break bb0;
    }
    let t92;
    if ($[5] !== chartProps || $[6] !== columns || $[7] !== data || $[8] !== isTransposedData || $[9] !== labels) {
      const chartData = isTransposedData ? transposedDataToSeries(chartProps, data, columns, labels) : transposeRowsToSeries(chartProps, data, columns, labels);
      const chartDataWithSums = chartData.map(_temp2);
      t92 = [...chartDataWithSums].sort(_temp3);
      $[5] = chartProps;
      $[6] = columns;
      $[7] = data;
      $[8] = isTransposedData;
      $[9] = labels;
      $[10] = t92;
    } else {
      t92 = $[10];
    }
    t8 = t92;
  }
  const orderedChartData = t8;
  let t9;
  if ($[11] !== colorScheme || $[12] !== orderedChartData || $[13] !== theme) {
    let t102;
    if ($[15] !== colorScheme || $[16] !== theme) {
      t102 = (_item, idx) => {
        if (_item.type === "line") {
          return theme.charts.stackedBar.lines[colorScheme || "default"][0] ?? theme.charts.line.colors.default[0] ?? "";
        }
        return theme.charts.stackedBar.bars[colorScheme || "default"][idx] ?? "";
      };
      $[15] = colorScheme;
      $[16] = theme;
      $[17] = t102;
    } else {
      t102 = $[17];
    }
    t9 = orderedChartData.map(t102);
    $[11] = colorScheme;
    $[12] = orderedChartData;
    $[13] = theme;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const colors = t9;
  let t10;
  bb1: {
    const hasRight = orderedChartData.some(_temp4);
    let t112;
    if ($[18] !== columns) {
      t112 = series_0 => makeValueFormatter(columns.find(c2 => c2.key === series_0.find(_temp5)?.columnKey), 0);
      $[18] = columns;
      $[19] = t112;
    } else {
      t112 = $[19];
    }
    const axisFormatter = t112;
    let t122;
    if ($[20] !== axisFormatter) {
      t122 = series_1 => {
        const len = series_1[0]?.data.length || 0;
        let max = 0;
        for (let i = 0; i < len; i++) {
          let sum_1 = 0;
          for (const s_1 of series_1) {
            if (s_1.type === "bar") {
              sum_1 = sum_1 + Number(s_1.data[i] ?? 0);
            }
          }
          if (sum_1 > max) {
            max = sum_1;
          }
        }
        const formatter = axisFormatter(series_1);
        const repr = formatter ? formatter(Math.ceil(max)) : Math.ceil(max).toString();
        return getYAxisWidth(repr.length);
      };
      $[20] = axisFormatter;
      $[21] = t122;
    } else {
      t122 = $[21];
    }
    const getStackedWidth = t122;
    if (!hasRight) {
      let t133;
      if ($[22] !== getStackedWidth || $[23] !== orderedChartData || $[24] !== showLabels) {
        t133 = showLabels ? getStackedWidth(orderedChartData) : 0;
        $[22] = getStackedWidth;
        $[23] = orderedChartData;
        $[24] = showLabels;
        $[25] = t133;
      } else {
        t133 = $[25];
      }
      let t143;
      if ($[26] !== axisFormatter || $[27] !== orderedChartData) {
        t143 = axisFormatter(orderedChartData);
        $[26] = axisFormatter;
        $[27] = orderedChartData;
        $[28] = t143;
      } else {
        t143 = $[28];
      }
      let t153;
      if ($[29] !== t133 || $[30] !== t143) {
        t153 = [{
          width: t133,
          valueFormatter: t143
        }];
        $[29] = t133;
        $[30] = t143;
        $[31] = t153;
      } else {
        t153 = $[31];
      }
      t10 = t153;
      break bb1;
    }
    let t132;
    let t142;
    let t152;
    let t162;
    let t172;
    if ($[32] !== axisFormatter || $[33] !== getStackedWidth || $[34] !== orderedChartData || $[35] !== showLabels) {
      const leftSeries = orderedChartData.filter(_temp6);
      const rightSeries = orderedChartData.filter(_temp7);
      const t183 = showLabels ? getStackedWidth(leftSeries) : 0;
      const t193 = axisFormatter(leftSeries);
      if ($[41] !== t183 || $[42] !== t193) {
        t172 = {
          id: "left",
          position: "left",
          width: t183,
          valueFormatter: t193
        };
        $[41] = t183;
        $[42] = t193;
        $[43] = t172;
      } else {
        t172 = $[43];
      }
      t132 = "right";
      t142 = "right";
      t152 = showLabels ? getStackedWidth(rightSeries) : 0;
      t162 = axisFormatter(rightSeries);
      $[32] = axisFormatter;
      $[33] = getStackedWidth;
      $[34] = orderedChartData;
      $[35] = showLabels;
      $[36] = t132;
      $[37] = t142;
      $[38] = t152;
      $[39] = t162;
      $[40] = t172;
    } else {
      t132 = $[36];
      t142 = $[37];
      t152 = $[38];
      t162 = $[39];
      t172 = $[40];
    }
    let t182;
    if ($[44] !== t132 || $[45] !== t142 || $[46] !== t152 || $[47] !== t162) {
      t182 = {
        id: t132,
        position: t142,
        width: t152,
        valueFormatter: t162
      };
      $[44] = t132;
      $[45] = t142;
      $[46] = t152;
      $[47] = t162;
      $[48] = t182;
    } else {
      t182 = $[48];
    }
    let t192;
    if ($[49] !== t172 || $[50] !== t182) {
      t192 = [t172, t182];
      $[49] = t172;
      $[50] = t182;
      $[51] = t192;
    } else {
      t192 = $[51];
    }
    t10 = t192;
  }
  const yAxes = t10;
  let t11;
  if ($[52] !== columns || $[53] !== orderedChartData) {
    let t122;
    if ($[55] !== columns) {
      t122 = s_4 => ({
        ...s_4,
        valueFormatter: makeValueFormatter(columns.find(c_0 => c_0.key === s_4.columnKey))
      });
      $[55] = columns;
      $[56] = t122;
    } else {
      t122 = $[56];
    }
    t11 = orderedChartData.map(t122);
    $[52] = columns;
    $[53] = orderedChartData;
    $[54] = t11;
  } else {
    t11 = $[54];
  }
  const chartSeriesWithFormatters = t11;
  if (!dataset || isLoading || !dataset?.data || dataset?.data.length === 0) {
    let t122;
    if ($[57] === /* @__PURE__ */Symbol.for("react.memo_cache_sentinel")) {
      t122 = /* @__PURE__ */jsx(Skeleton, {
        variant: "rectangular",
        width: "100%",
        height: 260
      });
      $[57] = t122;
    } else {
      t122 = $[57];
    }
    return t122;
  }
  let t12;
  if ($[58] !== columns || $[59] !== xKey) {
    let t132;
    if ($[61] !== xKey) {
      t132 = col => col.key === xKey;
      $[61] = xKey;
      $[62] = t132;
    } else {
      t132 = $[62];
    }
    t12 = columns.findIndex(t132);
    $[58] = columns;
    $[59] = xKey;
    $[60] = t12;
  } else {
    t12 = $[60];
  }
  const xIndex = t12;
  if (xIndex === -1 && columns.length) {
    throw new Error(`X-axis key '${xKey}' not found in dataset columns`);
  }
  let t13;
  if ($[63] !== data || $[64] !== xIndex) {
    let t142;
    if ($[66] !== xIndex) {
      t142 = row => String(row[xIndex]);
      $[66] = xIndex;
      $[67] = t142;
    } else {
      t142 = $[67];
    }
    t13 = Array.from(new Set(data.map(t142)));
    $[63] = data;
    $[64] = xIndex;
    $[65] = t13;
  } else {
    t13 = $[65];
  }
  const xAxisLabels = t13;
  let t14;
  if ($[68] === /* @__PURE__ */Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      textAlign: "center",
      fontSize: "0.75rem",
      fontWeight: 600,
      opacity: 0.5
    };
    $[68] = t14;
  } else {
    t14 = $[68];
  }
  let t15;
  if ($[69] !== title) {
    t15 = /* @__PURE__ */jsx(Typography, {
      sx: t14,
      children: title
    });
    $[69] = title;
    $[70] = t15;
  } else {
    t15 = $[70];
  }
  const footer = t15;
  let t16;
  if ($[71] === /* @__PURE__ */Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      width: "100%"
    };
    $[71] = t16;
  } else {
    t16 = $[71];
  }
  const t17 = height - 4;
  let t18;
  if ($[72] === /* @__PURE__ */Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      right: 0,
      left: 0,
      bottom: 0
    };
    $[72] = t18;
  } else {
    t18 = $[72];
  }
  const t19 = chartSeriesWithFormatters;
  let t20;
  if ($[73] === /* @__PURE__ */Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      horizontal: true,
      vertical: false
    };
    $[73] = t20;
  } else {
    t20 = $[73];
  }
  let t21;
  if ($[74] !== xAxisLabels) {
    t21 = [{
      id: "x",
      scaleType: "band",
      data: xAxisLabels,
      categoryGapRatio: 0.35
    }];
    $[74] = xAxisLabels;
    $[75] = t21;
  } else {
    t21 = $[75];
  }
  let t22;
  if ($[76] === /* @__PURE__ */Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      legend: {
        position: {
          vertical: "bottom"
        }
      }
    };
    $[76] = t22;
  } else {
    t22 = $[76];
  }
  const t23 = showLabels ? "flex" : "none";
  let t24;
  if ($[77] !== t23) {
    t24 = {
      "& .MuiChartsLegend-root": {
        maxWidth: "80%",
        display: t23
      }
    };
    $[77] = t23;
    $[78] = t24;
  } else {
    t24 = $[78];
  }
  let t25;
  if ($[79] !== colors || $[80] !== showLabels || $[81] !== t17 || $[82] !== t19 || $[83] !== t21 || $[84] !== t24 || $[85] !== yAxes) {
    t25 = /* @__PURE__ */jsx(StyledBarChart, {
      height: t17,
      margin: t18,
      series: t19,
      colors,
      showLabels,
      grid: t20,
      xAxis: t21,
      yAxis: yAxes,
      slotProps: t22,
      sx: t24
    });
    $[79] = colors;
    $[80] = showLabels;
    $[81] = t17;
    $[82] = t19;
    $[83] = t21;
    $[84] = t24;
    $[85] = yAxes;
    $[86] = t25;
  } else {
    t25 = $[86];
  }
  let t26;
  if ($[87] !== footer || $[88] !== t25) {
    t26 = /* @__PURE__ */jsxs(Stack, {
      sx: t16,
      children: [t25, footer]
    });
    $[87] = footer;
    $[88] = t25;
    $[89] = t26;
  } else {
    t26 = $[89];
  }
  return t26;
};
function _temp(sum, val) {
  return sum + (typeof val === "number" ? val : 0);
}
function _temp2(series) {
  if (series.type === "bar") {
    const sum_0 = series.data.reduce(_temp, 0);
    return {
      ...series,
      sum: sum_0
    };
  }
  return series;
}
function _temp3(a, b) {
  if (a.type === "bar" && b.type === "bar") {
    return (b.sum || 0) - (a.sum || 0);
  }
  return 0;
}
function _temp4(s) {
  return s.yAxisId === "right";
}
function _temp5(s_0) {
  return s_0.type === "bar";
}
function _temp6(s_2) {
  return s_2.yAxisId !== "right";
}
function _temp7(s_3) {
  return s_3.yAxisId === "right";
}
export { StackedBarChart as default };